import { useCallback, useEffect, useRef, useState } from 'react';
import { Campaign, User } from '../../../../types';
import { logGlobalError } from '../../../../utils/globalErrorLogger';
import { buildUsersListFilter, UsersFilterTab } from './filter';
import { buildAlignedSyncNotice } from './alignedSyncNotice';
import { createUsersRepository } from '../../../../platform/firebase/usersRepository';
import { createCampaignRepository } from '../../../../platform/firebase/campaignRepository';
import type {
  UserRolePatch,
  UsersPageCursor,
  UsersRepository,
} from '../../../../domain/users/repository';

const PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 250;

export interface UsersToolViewModel {
  busy: boolean;
  /** Set while a page fetch is in flight — for showing a subtler spinner. */
  paging: boolean;
  error: string | null;
  /**
   * Informational follow-up to the last role update — set when the change was
   * saved in Workbench but could not be mirrored to Aligned for some campaign.
   */
  notice: string | null;
  dismissNotice: () => void;
  campaigns: Campaign[];
  pageUsers: User[];
  /** 0-indexed page. */
  page: number;
  pageSize: number;
  /** Total docs matching the active filter+search, or `null` while unknown. */
  totalCount: number | null;
  /** Pages computed from `totalCount`; `null` until the count resolves. */
  totalPages: number | null;
  pendingUid: string | null;
  syncingUnsubscribeUid: string | null;
  query: string;
  setQuery: (q: string) => void;
  tab: UsersFilterTab;
  setTab: (t: UsersFilterTab) => void;
  campaignFilterId: string | null;
  setCampaignFilterId: (id: string | null) => void;
  nextPage: () => void;
  prevPage: () => void;
  initialize: () => void;
  dispose: () => void;
  updateRoles: (uid: string, patch: UserRolePatch) => Promise<void>;
  syncUnsubscribeToken: (uid: string) => Promise<void>;
}

export function useUsersToolViewModel(): UsersToolViewModel {
  const [busy, setBusy] = useState<boolean>(true);
  const [paging, setPaging] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [pageUsers, setPageUsers] = useState<User[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [pendingUid, setPendingUid] = useState<string | null>(null);
  const [syncingUnsubscribeUid, setSyncingUnsubscribeUid] = useState<string | null>(null);
  const [queryStr, setQueryStr] = useState<string>('');
  const [debouncedQuery, setDebouncedQuery] = useState<string>('');
  const [tab, setTabState] = useState<UsersFilterTab>('all');
  const [campaignFilterId, setCampaignFilterIdState] = useState<string | null>(null);

  // Per-page cursors: cursors[n] is the cursor that fetches page n. Page 0
  // uses no cursor, so cursors[0] is always null. We push the last doc of
  // page n as cursors[n+1] when that fetch completes — re-fetching a page
  // we've already visited reuses the same cursor without an extra read.
  const cursorsRef = useRef<Array<UsersPageCursor | null>>([null]);
  // Generation counter so a slow in-flight fetch can't overwrite the page
  // after the user has navigated or changed the filter. Bump on every fetch
  // start; only apply the result if the generation still matches when it
  // resolves.
  const fetchSeqRef = useRef<number>(0);
  const unsubsRef = useRef<Array<() => void>>([]);
  const usersRepositoryRef = useRef<UsersRepository | null>(null);
  if (!usersRepositoryRef.current) {
    usersRepositoryRef.current = createUsersRepository();
  }
  const usersRepository = usersRepositoryRef.current as UsersRepository;
  const campaignRepositoryRef = useRef<ReturnType<typeof createCampaignRepository> | null>(null);
  if (!campaignRepositoryRef.current) {
    campaignRepositoryRef.current = createCampaignRepository();
  }
  const campaignRepository = campaignRepositoryRef.current;

  // Debounce the search so the user typing doesn't fire a Firestore round
  // trip per keystroke. The empty-string case is applied immediately so
  // clearing the field doesn't sit on an empty page for 250ms.
  useEffect(() => {
    if (queryStr === '') {
      setDebouncedQuery('');
      return;
    }
    const t = setTimeout(() => setDebouncedQuery(queryStr), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [queryStr]);

  const dispose = useCallback(() => {
    unsubsRef.current.forEach((u) => u());
    unsubsRef.current = [];
  }, []);

  const resetPagination = useCallback(() => {
    cursorsRef.current = [null];
    setPage(0);
    setTotalCount(null);
  }, []);

  // Any filter / search / tab change throws away the cursor stack — the
  // result set is different now, so page indices and cached cursors no
  // longer line up.
  const setTab = useCallback((t: UsersFilterTab) => {
    setTabState(t);
    resetPagination();
  }, [resetPagination]);
  const setCampaignFilterId = useCallback((id: string | null) => {
    setCampaignFilterIdState(id);
    resetPagination();
  }, [resetPagination]);
  const setQuery = useCallback((q: string) => {
    setQueryStr(q);
    // The debounce effect will catch up; pagination resets here so the
    // about-to-fire fetch starts at page 0.
    resetPagination();
  }, [resetPagination]);

  // Mirror of `totalCount` for use inside `loadPage` without making the
  // callback depend on it (which would invalidate every memoized handler
  // built on top of it after each fetch).
  const totalCountRef = useRef<number | null>(null);
  useEffect(() => { totalCountRef.current = totalCount; }, [totalCount]);

  const loadPage = useCallback(async (
    targetPage: number,
    opts?: { recount?: boolean },
  ) => {
    const filter = buildUsersListFilter(tab, campaignFilterId);
    if (!filter) {
      // Campaign tab with no campaign picked yet — keep the page empty and
      // wait. The user has to choose a campaign before we can fetch.
      setPageUsers([]);
      setTotalCount(0);
      setPaging(false);
      return;
    }
    const seq = ++fetchSeqRef.current;
    setPaging(true);
    setError(null);
    const cachedCount = totalCountRef.current;
    // Recount on first page, when we don't have one yet, or when the caller
    // asks (e.g. after a write that may have moved a doc in/out of scope).
    const shouldRecount = opts?.recount === true || cachedCount === null || targetPage === 0;
    try {
      const cursor = cursorsRef.current[targetPage] ?? null;
      const [pageRes, count] = await Promise.all([
        usersRepository.fetchPage({
          filter,
          searchQuery: debouncedQuery.trim() || undefined,
          pageSize: PAGE_SIZE,
          cursor,
        }),
        shouldRecount
          ? usersRepository.countUsers(filter, debouncedQuery.trim() || undefined)
          : Promise.resolve(cachedCount as number),
      ]);
      if (seq !== fetchSeqRef.current) return;
      setPageUsers(pageRes.users);
      setTotalCount(count);
      setPage(targetPage);
      if (pageRes.lastCursor) {
        cursorsRef.current[targetPage + 1] = pageRes.lastCursor;
      }
    } catch (e: any) {
      if (seq !== fetchSeqRef.current) return;
      const msg = e?.message || 'Failed to load users';
      setError(msg);
      logGlobalError(e, {
        context: 'UsersTool.loadPage',
        metadata: { tab, campaignFilterId, page: targetPage },
      });
    } finally {
      if (seq === fetchSeqRef.current) setPaging(false);
    }
  }, [tab, campaignFilterId, debouncedQuery, usersRepository]);

  const initialize = useCallback(() => {
    setBusy(true);
    setError(null);
    dispose();

    const unsubCamps = campaignRepository.subscribeActive(
      (next) => {
        setCampaigns(next);
      },
      (err) => {
        setError(err.message);
      },
    );
    unsubsRef.current = [unsubCamps];

    // Kick off the first page; clears `busy` when it resolves.
    void loadPage(0).finally(() => setBusy(false));
    // We intentionally bind loadPage from its current closure — re-running
    // initialize on subsequent filter changes would tear down the campaigns
    // subscription, which we want kept alive for the whole tool session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignRepository, dispose]);

  // Whenever the active filter changes (and the user is past initial load),
  // refetch page 0. The reset already cleared cursors and bumped page to 0;
  // loadPage(0) is what actually populates the new view.
  useEffect(() => {
    if (busy) return;
    void loadPage(0);
    // loadPage closes over the same deps; we re-fire when those change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, campaignFilterId, debouncedQuery]);

  const totalPages = totalCount === null
    ? null
    : Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const nextPage = useCallback(() => {
    if (totalPages !== null && page >= totalPages - 1) return;
    void loadPage(page + 1);
  }, [page, totalPages, loadPage]);
  const prevPage = useCallback(() => {
    if (page === 0) return;
    void loadPage(page - 1);
  }, [page, loadPage]);

  const refreshCurrentPage = useCallback(async () => {
    // After a role write the doc may have moved in or out of the result
    // set, which can also shift later cursors. Drop cursors beyond the
    // current page and re-fetch from the page we're on, forcing a fresh
    // count so the pagination footer stays in sync.
    cursorsRef.current = cursorsRef.current.slice(0, page + 1);
    await loadPage(page, { recount: true });
  }, [page, loadPage]);

  const dismissNotice = useCallback(() => setNotice(null), []);

  const updateRoles = useCallback(async (uid: string, patch: UserRolePatch) => {
    setPendingUid(uid);
    setError(null);
    setNotice(null);
    try {
      const result = await usersRepository.updateUserRoles(uid, patch);
      // The command mirrors reviewer-campaign changes onto Aligned before it
      // saves; tell the admin when some campaign could not be mirrored.
      setNotice(buildAlignedSyncNotice(result.alignedSync, campaigns));
      // Optimistically update the row so the UI reflects the change without
      // waiting for the refetch round trip; the refetch resolves any drift.
      setPageUsers((prev) => prev.map((u) => (u.uid === uid ? { ...u, ...patch } : u)));
      await refreshCurrentPage();
    } catch (e: any) {
      setError(e?.message || 'Failed to update user');
      logGlobalError(e, {
        context: 'UsersTool.updateRoles',
        metadata: { targetUid: uid, fields: Object.keys(patch) },
      });
      throw e;
    } finally {
      setPendingUid(null);
    }
  }, [campaigns, refreshCurrentPage, usersRepository]);

  const syncUnsubscribeToken = useCallback(async (uid: string) => {
    setSyncingUnsubscribeUid(uid);
    setError(null);
    try {
      await usersRepository.syncUserUnsubscribeToken(uid);
      await refreshCurrentPage();
    } catch (e: any) {
      setError(e?.message || 'Failed to sync unsubscribe token');
      logGlobalError(e, {
        context: 'UsersTool.syncUnsubscribeToken',
        metadata: { targetUid: uid },
      });
      throw e;
    } finally {
      setSyncingUnsubscribeUid(null);
    }
  }, [refreshCurrentPage, usersRepository]);

  return {
    busy,
    paging,
    error,
    notice,
    dismissNotice,
    campaigns,
    pageUsers,
    page,
    pageSize: PAGE_SIZE,
    totalCount,
    totalPages,
    pendingUid,
    syncingUnsubscribeUid,
    query: queryStr,
    setQuery,
    tab,
    setTab,
    campaignFilterId,
    setCampaignFilterId,
    nextPage,
    prevPage,
    initialize,
    dispose,
    updateRoles,
    syncUnsubscribeToken,
  };
}
